/**
 * Acqs gallery bulk photo delete — fullscreen "delete mode".
 *
 * The "Delete photos" control on the images card
 * (`_property_images_card.html`) opens a fullscreen SeeMe modal whose body is
 * fetched fresh from `acqs:images-delete-mode` (room-grouped grid of checkbox
 * tiles — `_images_delete_mode.html`).  Selection is the only interaction:
 * tapping a tile toggles its checkbox natively (label/for), Select all and
 * per-room section-label toggles flip whole groups, and the header shows a
 * live "N selected" count.  Delete confirms via the data-confirm-delete idiom
 * and POSTs all checked ids through the button's HTMX attributes to the
 * grid's data-bulk-delete-url — the response re-renders `#property-images`
 * (+ OOB tab counts + toast) and the modal closes, restoring any underlying
 * sheet (fullscreen modals stack above sheets, see seeme/modals.js).
 *
 * The modal shell ships inside the card template but is moved to
 * document.body on first open: cards can live inside sheets, whose CSS
 * transform would trap the fixed-position modal.  The body-level shell is a
 * generic singleton reused by whichever card opens it — the delete-mode URL
 * travels on the trigger button, the bulk-delete URL on the loaded grid.
 */

(function () {

    var SKELETON =
        '<div class="rf-skeleton rf-skeleton--heading"></div>' +
        '<div class="rf-skeleton rf-skeleton--text"></div>' +
        '<div class="rf-skeleton rf-skeleton--text"></div>' +
        '<div class="rf-skeleton rf-skeleton--text rf-skeleton--short"></div>';

    var LOAD_ERROR =
        '<div class="empty-state">' +
        '<i class="fa-solid fa-triangle-exclamation"></i>' +
        '<span>Could not load photos — close and try again</span>' +
        '</div>';

    function getModal(btn) {
        var modal = document.querySelector('body > [data-images-delete-modal]');
        if (modal) return modal;
        var card = btn.closest('[data-image-uploader]');
        modal = card && card.querySelector('[data-images-delete-modal]');
        if (modal) document.body.appendChild(modal);
        return modal;
    }

    function getGrid(modal) {
        return modal.querySelector('[data-images-delete-mode]');
    }

    function refreshState(modal) {
        var grid = getGrid(modal);
        var total = grid ? parseInt(grid.getAttribute('data-image-total'), 10) || 0 : 0;
        var checked = grid ? grid.querySelectorAll('input[name="ids"]:checked').length : 0;

        var countEl = modal.querySelector('[data-delete-count]');
        if (countEl) countEl.textContent = checked + ' selected';

        var selectAll = modal.querySelector('[data-delete-select-all]');
        if (selectAll) {
            selectAll.textContent = total > 0 && checked === total ? 'Deselect all' : 'Select all';
            selectAll.disabled = total === 0;
        }

        if (grid) {
            // Per-room toggle badges mirror the Select all label: "Deselect"
            // only when every image in that room is checked.
            grid.querySelectorAll('[data-image-room-section]').forEach(function (section) {
                var badge = section.querySelector('[data-room-toggle-badge]');
                if (!badge) return;
                var boxes = section.querySelectorAll('input[name="ids"]');
                var allChecked = boxes.length > 0 &&
                    section.querySelectorAll('input[name="ids"]:checked').length === boxes.length;
                badge.textContent = allChecked ? 'Deselect' : 'Select';
            });
        }

        var del = modal.querySelector('[data-delete-submit]');
        if (del) {
            del.disabled = checked === 0;
            del.setAttribute(
                'data-confirm-label',
                'Delete ' + checked + ' photo' + (checked === 1 ? '' : 's') + '?'
            );
        }
    }

    function toggleGroup(modal, scope) {
        var boxes = scope.querySelectorAll('input[name="ids"]');
        if (!boxes.length) return;
        var check = scope.querySelectorAll('input[name="ids"]:checked').length !== boxes.length;
        boxes.forEach(function (cb) { cb.checked = check; });
        refreshState(modal);
    }

    function openDeleteMode(btn) {
        if (!window.SeeMe || !SeeMe.overlay || !window.htmx) return;
        var modal = getModal(btn);
        if (!modal) return;

        // Fresh content every open — never a stale grid from a prior card.
        var body = modal.querySelector('[data-delete-modal-body]');
        body.innerHTML = SKELETON;
        refreshState(modal);
        SeeMe.overlay.open(modal);

        htmx.ajax('GET', btn.getAttribute('data-delete-mode-url'), {
            target: body,
            swap: 'innerHTML',
        }).then(function () {
            // 4xx/5xx responses still resolve this promise without swapping —
            // no grid means the load failed and the skeleton is still up.
            var grid = getGrid(modal);
            var del = modal.querySelector('[data-delete-submit]');
            if (!grid) {
                body.innerHTML = LOAD_ERROR;
            } else if (del) {
                del.setAttribute('hx-post', grid.getAttribute('data-bulk-delete-url'));
                htmx.process(del);
            }
            refreshState(modal);
        }).catch(function () {
            // Network error / send abort — same dead end as a failed response.
            body.innerHTML = LOAD_ERROR;
            refreshState(modal);
        });
    }

    document.addEventListener('click', function (e) {
        var openBtn = e.target.closest('[data-delete-photos]');
        if (openBtn) {
            e.preventDefault();
            openDeleteMode(openBtn);
            return;
        }

        var selectAll = e.target.closest('[data-delete-select-all]');
        if (selectAll) {
            var modal = selectAll.closest('[data-images-delete-modal]');
            var grid = modal && getGrid(modal);
            if (grid) toggleGroup(modal, grid);
            return;
        }

        var label = e.target.closest('[data-images-delete-mode] [data-image-room-section] .section-label');
        if (label) {
            toggleGroup(label.closest('[data-images-delete-modal]'), label.closest('[data-image-room-section]'));
        }
    });

    document.addEventListener('change', function (e) {
        if (!e.target.matches('[data-images-delete-mode] input[name="ids"]')) return;
        var modal = e.target.closest('[data-images-delete-modal]');
        if (modal) refreshState(modal);
    });

    // Bulk delete succeeded — the response already swapped #property-images
    // (+ OOB counts, toast via HX-Trigger); close the modal, restoring any
    // sheet underneath.
    document.addEventListener('htmx:afterRequest', function (e) {
        var el = e.detail.elt;
        if (!el || !el.matches || !el.matches('[data-delete-submit]')) return;
        if (!e.detail.successful) {
            // Modal stays open, hx-disabled-elt re-enables the button — just
            // surface the failure (a stale selection 400s server-side when
            // photos were deleted elsewhere mid-session). csrf.js already
            // toasts 403s.
            var status = e.detail.xhr && e.detail.xhr.status;
            if (status !== 403 && window.SeeMe && SeeMe.toast) {
                SeeMe.toast.error('Could not delete photos — close and try again');
            }
            return;
        }
        var modal = el.closest('[data-images-delete-modal]');
        if (modal && window.SeeMe && SeeMe.overlay) SeeMe.overlay.close(modal);
    });

})();
